import { ButtonBase, Grid, styled, Typography } from "@mui/material";
import { observer } from "mobx-react";
import React, { FunctionComponent, useState } from "react";
import theme from "../../../../Theme/AppTheme";
import { ChatPersona, IAugmentedChatRecord } from "../AcxChatViewer";
import { ChatViewModel } from "../ViewModel/ChatViewModel";
import moment from "moment";
import ScissorsSvg from "SvgIcons/ScissorsSvg";
import EddyChip from "./EddyChip";
import { getSpeakerColorByChannelOrPersona } from "Theme/utils";
import { useStore } from "utils/useStore";
import { AcxTranscriptionStore } from "components/UI/AcxTranscription/Stores/AcxTranscriptionStore";
import parser from "html-react-parser";

interface OwnProps {
    record: IAugmentedChatRecord;
    index: number;
    viewModel?: ChatViewModel;
    disableClip?: boolean;
}

type Props = OwnProps;

const NoSelectGrid = styled(Grid)(({ theme }) => ({
    WebkitTouchCallout: "none",
    WebkitUserSelect: "none",
    KhtmlUserSelect: "none",
    MozUserSelect: "none",
    MsUserSelect: "none",
    UserSelect: "none",
}));

const ChatMessage: FunctionComponent<Props> = observer((props) => {
    const store = useStore(AcxTranscriptionStore);

    const inClipSet = props.viewModel?.clipSet.has(props.index);

    const [hovering, setHovering] = useState(false);

    return (
        <Grid
            sx={{
                display: "flex",
                alignItems: "center",
                flexDirection: "row",
                gap: 1,
            }}
            onMouseEnter={() => setHovering(true)}
            onMouseLeave={() => setHovering(false)}
        >
            {props.record.from === ChatPersona.Agent && (
                <SpeakerAndTimestamp record={props.record} />
            )}
            {props.record.from === ChatPersona.Customer && (
                <NoSelectGrid
                    item
                    sx={{ display: "flex", alignItems: "center" }}
                    className="highlightIgnore"
                >
                    <ClipButton
                        record={props.record}
                        disableClip={props.disableClip}
                        viewModel={props.viewModel}
                        index={props.index}
                        hovering={hovering}
                        inClipSet={inClipSet}
                    />
                </NoSelectGrid>
            )}
            <Grid item zeroMinWidth id={props.record.timestamp}>
                <div
                    style={
                        props.record.from === ChatPersona.Agent
                            ? {
                                  borderWidth: props.record.clipId
                                      ? "2px"
                                      : "unset",
                                  borderStyle: props.record.clipId
                                      ? "solid"
                                      : "none",
                                  borderColor: props.record.clipId
                                      ? props.record.color
                                      : "unset",
                                  borderRadius: "8px 8px 8px 0",
                                  backgroundColor: "#F9FAFB",
                              }
                            : {
                                  borderWidth: props.record.clipId
                                      ? "2px"
                                      : "unset",
                                  borderStyle: props.record.clipId
                                      ? "solid"
                                      : "none",
                                  borderColor: props.record.clipId
                                      ? props.record.color
                                      : "unset",
                                  borderRadius: "8px 8px 0 8px",
                                  backgroundColor: "#F9FAFB",
                              }
                    }
                    onMouseEnter={(event) =>
                        props.viewModel?.onFocusClip?.(props.record.clipId)
                    }
                    onMouseLeave={(event) =>
                        props.viewModel?.onFocusClip?.(undefined)
                    }
                >
                    <Typography
                        sx={{
                            padding: "8px 24px 8px 16px",
                            color: theme.palette.text.primary,
                            wordWrap: "break-word",
                            whiteSpace: "pre-wrap",
                            overflowWrap: "break-word",
                            fontFamily: theme.typography.fontFamily,
                            fontSize: "13px",
                            lineHeight: "20px",
                            letterSpacing: 0,
                            width: "100%",
                            height: "100%",
                            textAlign: "left",
                        }}
                        className={"pendo-ignore"}
                    >
                        {parser(
                            store.formatTextWithHighlights(
                                props.record.words
                                    .map((word) => word.word)
                                    .join(" "),
                            ),
                        )}
                    </Typography>
                    {props.record.eddy && (
                        <EddyChip
                            containerStyle={{
                                padding: "0px 24px 8px 16px",
                                userSelect: "none",
                                WebkitTouchCallout: "none",
                                WebkitUserSelect: "none",
                                KhtmlUserSelect: "none",
                                MozUserSelect: "none",
                            }}
                        />
                    )}
                </div>
            </Grid>
            {props.record.from === ChatPersona.Agent && (
                <NoSelectGrid
                    item
                    sx={{ display: "flex", alignItems: "center" }}
                    className="highlightIgnore"
                >
                    <ClipButton
                        record={props.record}
                        disableClip={props.disableClip}
                        viewModel={props.viewModel}
                        index={props.index}
                        hovering={hovering}
                        inClipSet={inClipSet}
                    />
                </NoSelectGrid>
            )}
            {props.record.from === ChatPersona.Customer && (
                <SpeakerAndTimestamp record={props.record} />
            )}
        </Grid>
    );
});

const SpeakerAndTimestamp = ({ record }: { record: IAugmentedChatRecord }) => {
    return (
        <Grid item id={record.timestamp}>
            {record.from === ChatPersona.Agent ? (
                <Typography
                    sx={{
                        color: getSpeakerColorByChannelOrPersona(
                            ChatPersona.Agent,
                        ),
                        fontSize: theme.typography.body1.fontSize,
                        fontWeight: 700,
                    }}
                >
                    Agent
                </Typography>
            ) : (
                <Typography
                    sx={{
                        color: getSpeakerColorByChannelOrPersona(
                            ChatPersona.Customer,
                        ),
                        fontSize: theme.typography.body1.fontSize,
                        fontWeight: 700,
                    }}
                >
                    Customer
                </Typography>
            )}
            {record.timestamp && (
                <Typography>
                    {moment(record.timestamp).format("hh:mm")}
                </Typography>
            )}
        </Grid>
    );
};

const ClipButton = ({
    record,
    disableClip,
    viewModel,
    index,
    hovering,
    inClipSet,
}) => {
    return (
        !record.clipId &&
        !disableClip && (
            <ButtonBase
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    border: "solid 1px #E4E4E7",
                    backgroundColor: "transparent",
                    margin: 0,
                    height: "26px",
                    width: "26px",
                    borderRadius: 1,
                    visibility: hovering || inClipSet ? "visible" : "hidden",
                }}
                onClick={(event) => viewModel?.toggleRecordToClipSet(index)}
            >
                <ScissorsSvg width={"16px"} height={"16px"} />
            </ButtonBase>
        )
    );
};

export default ChatMessage;
